import { PhoneIcon } from "@heroicons/react/24/outline";

import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import ScreenChrome from "components/ScreenChrome";

import { outOfAreaCallLine, outOfAreaCopy } from "../copy";
import { isBlockingSegment, type Segment } from "../flow";

/** The interstitial for a ZIP we do not serve. An at-need ZIP is a dead
 * end: the visitor can only change the ZIP, and the call line is offered.
 * A pre-need ZIP may be passed, to the other options, once the screen has
 * said what continuing does. `onBack` returns to the step that asks for
 * the ZIP. */
export default function OutOfAreaStep({
  segment,
  onBack,
  onContinue,
}: {
  segment: Segment;
  onBack: () => void;
  onContinue: () => void;
}) {
  const blocked = isBlockingSegment(segment);
  const copy = outOfAreaCopy();
  const line = outOfAreaCallLine(segment);
  return (
    <ScreenChrome
      kicker={copy.kicker}
      titleSize="compact"
      titleA={copy.titleA}
      titleB={copy.titleB}
      blurb={copy.blurb}
    >
      <div className="mt-7 flex flex-col gap-5">
        {line && (
          <div className="flex items-start gap-3 rounded-xl border border-oat-200 bg-white px-4 py-3 text-base leading-snug text-soil">
            <PhoneIcon
              className="mt-0.5 h-5 w-5 flex-none text-evergreen"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span>
              Have questions? Call us at{" "}
              <a href={line.href} className="ph-no-capture font-bold underline">
                {line.label}
              </a>
              . We&rsquo;re here 24/7.
            </span>
          </div>
        )}
        {!blocked && (
          <p className="text-base leading-normal text-oat-700">
            We can’t offer a plan for this ZIP yet. If you continue, we’ll save
            your details, show you what we can do now, and email you when we
            open in your area.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button type="button" className={LINK_BUTTON_CLASS} onClick={onBack}>
            Change ZIP
          </button>
          {!blocked && (
            <button
              type="button"
              className={`${CONTINUE_BUTTON_CLASS} ml-auto`}
              onClick={onContinue}
            >
              Continue
            </button>
          )}
        </div>
      </div>
    </ScreenChrome>
  );
}
